// Generated by TaroBase SDK Generator: 2026-07-15T20:57:35.995Z
// Collection: vendors/$vendorId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * A vendor's editable public profile for the Creve FUTO marketplace. Keyed by the vendor's wallet address. Includes an admin-only verified flag and a fullName lock once verified.
 */

export interface VendorsRequest {
  owner: AddressType;
  fullName: string;
  bio?: string;
  department?: string;
  level?: string;
  phone?: string;
  contactEmail?: string;
  avatar?: string;
  verified: boolean;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
  updatedAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface VendorsResponse {
  owner: string;
  fullName: string;
  bio?: string;
  department?: string;
  level?: string;
  phone?: string;
  contactEmail?: string;
  avatar?: string;
  verified: boolean;
  createdAt: number;
  updatedAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a Vendors operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildVendors(vendorId: string, data: VendorsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `vendors/${vendorId}`, document: doc };
}

/**
 * Authenticated vendor creating their own record only. The document ID ($vendorId) must equal the caller's wallet address, @newData.owner must equal the caller, and verified must be created as false (vendors cannot self-verify). createdAt/updatedAt are Unix-seconds timestamps supplied by the client. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setVendors(vendorId: string, data: VendorsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`vendors/${vendorId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting Vendors: ${formatError(error)}`);
    return false;
  }
}

export type VendorsRequestUpdate = Partial<VendorsRequest>;

/**
 * Build a Vendors update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateVendors(vendorId: string, data: VendorsRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `vendors/${vendorId}`, document: doc };
}

/**
 * Allowed by the profile owner (owner == caller) OR by @constants.ADMIN_ADDRESS. Owner updates: verified is locked (@newData.verified must equal existing @data.verified, so owners can never flip it); and if the record is already verified, fullName is locked to the existing value. Admin updates bypass both locks, which is the only path that can toggle verified. owner is immutable (Address! readonly modifier). (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateVendors(vendorId: string, data: VendorsRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`vendors/${vendorId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating Vendors: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Public. Anyone (including logged-out users) can read all vendor profile fields so verified badges render everywhere.
   (Get Single Item)
 */
export async function getVendors(vendorId: string): Promise<VendorsResponse | null> {
  try {
    const result = await get(`vendors/${vendorId}`);
    return result as VendorsResponse | null;
  } catch (error) {
    console.error(`Error getting Vendors: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single Vendors document. (
  Read Operation Details: Public. Anyone (including logged-out users) can read all vendor profile fields so verified badges render everywhere.
  )
 */
export function subscribeVendors(callback: (data: VendorsResponse | null) => void, vendorId: string): Promise<() => Promise<void>> {
  return subscribe(`vendors/${vendorId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as VendorsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to Vendors at path '${`vendors/${vendorId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many Vendors items from collection vendors
 
  Read Operation Details: Public. Anyone (including logged-out users) can read all vendor profile fields so verified badges render everywhere.
  
 */
export async function getManyVendors(filter: string = ""): Promise<VendorsResponse[]> {
  try {
    const results = await get("vendors", { prompt: filter });
    return Array.isArray(results) ? results as VendorsResponse[] : [];
  } catch (error) {
    console.error(`Error getting Vendors collection at path 'vendors': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Vendors collection at vendors
 
  Read Operation Details: Public. Anyone (including logged-out users) can read all vendor profile fields so verified badges render everywhere.
  
 */
export function subscribeManyVendors(callback: (data: VendorsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("vendors", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as VendorsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Vendors collection at path '${"vendors"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all Vendors items from collection vendors
 
  Read Operation Details: Public. Anyone (including logged-out users) can read all vendor profile fields so verified badges render everywhere.
  
 */
export async function getAllVendors(filter: string = ""): Promise<VendorsResponse[]> {
  try {
    const results = await get("vendors", { prompt: filter });
    return Array.isArray(results) ? results as VendorsResponse[] : [];
  } catch (error) {
    console.error(`Error getting Vendors collection at path 'vendors': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Vendors collection at vendors
 
  Read Operation Details: Public. Anyone (including logged-out users) can read all vendor profile fields so verified badges render everywhere.
  
 */
export function subscribeAllVendors(callback: (data: VendorsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("vendors", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as VendorsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Vendors collection at path '${"vendors"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count Vendors items in collection vendors.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countVendors(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("vendors", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting Vendors at path 'vendors': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on Vendors items in collection vendors.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateVendors(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("vendors", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating Vendors at path 'vendors': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: The owner or the admin may delete a vendor profile.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteVendors(vendorId: string): Promise<boolean> {
  try {
    const succeeded = await set(`vendors/${vendorId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting Vendors: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for Vendors for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteVendors(vendorId: string): DocumentOperation {
  return { path: `vendors/${vendorId}`, document: null };
}


/**
 * Dynamic social link rows for a vendor (Poof has no array fields). Each row is a label + url. Managed only by the parent vendor's owner.
 */

export interface VendorsSocialLinksRequest {
  label: string;
  url: string;
}

export interface VendorsSocialLinksResponse {
  label: string;
  url: string;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a VendorsSocialLinks operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildVendorsSocialLinks(vendorId: string, linkId: string, data: VendorsSocialLinksRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `vendors/${vendorId}/socialLinks/${linkId}`, document: doc };
}

/**
 * Only the parent vendor's owner may add a social link (the $vendorId path segment, which is the vendor's wallet address, must equal the caller's wallet). (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setVendorsSocialLinks(vendorId: string, linkId: string, data: VendorsSocialLinksRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`vendors/${vendorId}/socialLinks/${linkId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting VendorsSocialLinks: ${formatError(error)}`);
    return false;
  }
}

export type VendorsSocialLinksRequestUpdate = Partial<VendorsSocialLinksRequest>;

/**
 * Build a VendorsSocialLinks update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateVendorsSocialLinks(vendorId: string, linkId: string, data: VendorsSocialLinksRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `vendors/${vendorId}/socialLinks/${linkId}`, document: doc };
}

/**
 * Only the parent vendor's owner (wallet in $vendorId == caller) may edit a social link. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateVendorsSocialLinks(vendorId: string, linkId: string, data: VendorsSocialLinksRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`vendors/${vendorId}/socialLinks/${linkId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating VendorsSocialLinks: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Public. Anyone can read a vendor's social links.
   (Get Single Item)
 */
export async function getVendorsSocialLinks(vendorId: string, linkId: string): Promise<VendorsSocialLinksResponse | null> {
  try {
    const result = await get(`vendors/${vendorId}/socialLinks/${linkId}`);
    return result as VendorsSocialLinksResponse | null;
  } catch (error) {
    console.error(`Error getting VendorsSocialLinks: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single VendorsSocialLinks document. (
  Read Operation Details: Public. Anyone can read a vendor's social links.
  )
 */
export function subscribeVendorsSocialLinks(callback: (data: VendorsSocialLinksResponse | null) => void, vendorId: string, linkId: string): Promise<() => Promise<void>> {
  return subscribe(`vendors/${vendorId}/socialLinks/${linkId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as VendorsSocialLinksResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to VendorsSocialLinks at path '${`vendors/${vendorId}/socialLinks/${linkId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many VendorsSocialLinks items from collection vendors/${vendorId}/socialLinks
 
  Read Operation Details: Public. Anyone can read a vendor's social links.
  
 */
export async function getManyVendorsSocialLinks(vendorId: string, filter: string = ""): Promise<VendorsSocialLinksResponse[]> {
  try {
    const results = await get(`vendors/${vendorId}/socialLinks`, { prompt: filter });
    return Array.isArray(results) ? results as VendorsSocialLinksResponse[] : [];
  } catch (error) {
    console.error(`Error getting VendorsSocialLinks collection at path 'vendors/${vendorId}/socialLinks': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in VendorsSocialLinks collection at vendors/${vendorId}/socialLinks
 
  Read Operation Details: Public. Anyone can read a vendor's social links.
  
 */
export function subscribeManyVendorsSocialLinks(callback: (data: VendorsSocialLinksResponse[]) => void, vendorId: string, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe(`vendors/${vendorId}/socialLinks`, {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as VendorsSocialLinksResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to VendorsSocialLinks collection at path '${`vendors/${vendorId}/socialLinks`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all VendorsSocialLinks items from collection vendors/${vendorId}/socialLinks
 
  Read Operation Details: Public. Anyone can read a vendor's social links.
  
 */
export async function getAllVendorsSocialLinks(vendorId: string, filter: string = ""): Promise<VendorsSocialLinksResponse[]> {
  try {
    const results = await get(`vendors/*/socialLinks`, { prompt: filter });
    return Array.isArray(results) ? results as VendorsSocialLinksResponse[] : [];
  } catch (error) {
    console.error(`Error getting VendorsSocialLinks collection at path 'vendors/${vendorId}/socialLinks': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in VendorsSocialLinks collection at vendors/${vendorId}/socialLinks
 
  Read Operation Details: Public. Anyone can read a vendor's social links.
  
 */
export function subscribeAllVendorsSocialLinks(callback: (data: VendorsSocialLinksResponse[]) => void, vendorId: string, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe(`vendors/*/socialLinks`, {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as VendorsSocialLinksResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to VendorsSocialLinks collection at path '${`vendors/*/socialLinks`}': ${formatError(error)}`);
    }
  });
}

/**
 * Count VendorsSocialLinks items in collection vendors/${vendorId}/socialLinks.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countVendorsSocialLinks(vendorId: string, filter: string = ""): Promise<AggregateResult> {
  try {
    return await count(`vendors/${vendorId}/socialLinks`, { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting VendorsSocialLinks at path 'vendors/${vendorId}/socialLinks': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on VendorsSocialLinks items in collection vendors/${vendorId}/socialLinks.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateVendorsSocialLinks(vendorId: string, operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate(`vendors/${vendorId}/socialLinks`, operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating VendorsSocialLinks at path 'vendors/${vendorId}/socialLinks': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Only the parent vendor's owner (wallet in $vendorId == caller) may remove a social link.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteVendorsSocialLinks(vendorId: string, linkId: string): Promise<boolean> {
  try {
    const succeeded = await set(`vendors/${vendorId}/socialLinks/${linkId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting VendorsSocialLinks: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for VendorsSocialLinks for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteVendorsSocialLinks(vendorId: string, linkId: string): DocumentOperation {
  return { path: `vendors/${vendorId}/socialLinks/${linkId}`, document: null };
}

